// Napisy interfejsu za przełącznikiem EN/PL (patch-33). `T` w każdym pliku ekranu: `const T = { next: { en: 'Next', pl: 'Dalej' }, … } as const satisfies Record<string, Pair>`.
//  • `langOf(ctx)` — bieżący język (bez `ctx.teacher` — polski, jak w testach widoków na atrapach);
//  • `tr(ctx, pair)` — napis w bieżącym języku z formami płci dziecka (`{y|a}`);
//  • `tx(ctx, pair | (lang) => string)` — element <span>, który sam przepisuje napis po zmianie języka (do przycisków, nagłówków i etykiet, które zostają na ekranie);
//  • `txAttr(ctx, el, 'title', pair)` — to samo dla atrybutu (title, aria-label, placeholder);
//  • `onLang(ctx, fn)` — własny nasłuch zmiany języka (zwraca funkcję odpinającą);
//  • `rerenderOnLang(ctx, root, render)` — ekran bez stanu, który po zmianie języka rysujemy od nowa.
// Nasłuch elementu odpina się sam, gdy elementu nie ma już na stronie.
import { FALLBACK_LANG } from '../lib/i18n.ts';
import type { Pair } from '../lib/i18n.ts';
import type { TeacherLang } from '../types.ts';
import type { AppCtx } from './ctx.ts';
import { say } from './forms.ts';
import { h } from './dom.ts';

export type { Pair } from '../lib/i18n.ts';

type Text = Pair | ((lang: TeacherLang) => string);
type Ctx = Pick<AppCtx, 'teacher' | 'account'>;

export const langOf = (ctx: Pick<AppCtx, 'teacher'>): TeacherLang => ctx.teacher?.lang() ?? FALLBACK_LANG;

const resolve = (ctx: Ctx, text: Text): string => {
  const lang = langOf(ctx);
  return say(ctx, typeof text === 'function' ? text(lang) : text[lang]);
};

/** Napis w bieżącym języku (z formami płci). */
export const tr = (ctx: Ctx, text: Text): string => resolve(ctx, text);

const gone = (el: Element): boolean => (el as { isConnected?: boolean }).isConnected === false;

/** Własny nasłuch zmiany języka; bez `ctx.teacher` nic nie robi. */
export function onLang(ctx: Pick<AppCtx, 'teacher'>, fn: (lang: TeacherLang) => void): () => void {
  return ctx.teacher ? ctx.teacher.onChange(fn) : () => {};
}

/** <span>, który sam przepisuje napis po zmianie EN/PL. */
export function tx(ctx: Ctx, text: Text, cls?: string): HTMLSpanElement {
  const el = h('span', cls ? { class: cls } : null, resolve(ctx, text));
  if (ctx.teacher) {
    const off = ctx.teacher.onChange(() => {
      if (gone(el)) off();
      else el.textContent = resolve(ctx, text);
    });
  }
  return el;
}

/** Atrybut elementu (title, aria-label, placeholder…) w bieżącym języku; przepisuje się po zmianie EN/PL. */
export function txAttr<T extends Element>(ctx: Ctx, el: T, attr: string, text: Text): T {
  el.setAttribute(attr, resolve(ctx, text));
  if (ctx.teacher) {
    const off = ctx.teacher.onChange(() => {
      if (gone(el)) off();
      else el.setAttribute(attr, resolve(ctx, text));
    });
  }
  return el;
}

/** Tekst (textContent) istniejącego elementu, przepisywany po zmianie EN/PL. */
export function txText<T extends Element>(ctx: Ctx, el: T, text: Text): T {
  el.textContent = resolve(ctx, text);
  if (ctx.teacher) {
    const off = ctx.teacher.onChange(() => {
      if (gone(el)) off();
      else el.textContent = resolve(ctx, text);
    });
  }
  return el;
}

/** Ekran bez własnego stanu: po zmianie języka rysujemy go od nowa (`render` ma sam podmienić zawartość ekranu). */
export function rerenderOnLang(ctx: Pick<AppCtx, 'teacher'>, anchor: Element, render: () => void): void {
  if (!ctx.teacher) return;
  const off = ctx.teacher.onChange(() => {
    off();
    if (!gone(anchor)) render();
  });
}
